import { Heading1, Heading2 } from 'components/shared/Typography';
import styled from 'styled-components';
import {
  color,
  container,
  fontFamily,
  fontSize,
  fontWeight,
  radius,
  screen,
  spacing
} from 'theme';
import { getPublicPath } from 'utils/paths';

export const StyledWrapper = styled.div`
  background-color: ${color('indigo_100')};

  &.notice {
    background-color: ${color('indigo_500')};
    color: ${color('white')};

    ${Heading2}::before {
      background-color: ${color('white')};
    }
  }
`;

export const StyledContainer = styled.div`
  max-width: ${container('xl')};
  padding: ${spacing('xl5')} ${spacing('md')};
  margin: auto;
`;

export const StyledBillboard = styled.div`
  background-image: ${() =>
    `url('${getPublicPath(
      '/images/stateOfHiring/pyramids.svg'
    )}'), linear-gradient(296.21deg, #b93a98 -56.41%, #472ca6 100%)`};
  background-repeat: no-repeat;
  background-position: bottom -50% right, top right;
  background-size: 70%, cover;
  color: ${color('white')};
  font-size: ${fontSize('lg')};
  line-height: 1.6;
  text-align: center;

  @media (min-width: ${screen('md')}) {
    text-align: left;
    font-size: ${fontSize('xl2')};
    line-height: 1.58;
    background-position: top right -20%, top right;
    background-size: auto 130%, cover;
  }

  p {
    margin-bottom: 0;
  }

  ${StyledContainer} {
    padding-top: ${spacing('xl4')};
    padding-bottom: ${spacing('xl4')};

    @media (min-width: ${screen('md')}) {
      padding-top: ${spacing('xl6')};
      padding-bottom: ${spacing('xl6')};
    }
  }
`;

export const StyledBillboardContent = styled.div`
  max-width: 42rem;
`;

export const StyledBillboardTitle = styled(Heading1)`
  font-size: ${fontSize('xl5')};

  @media (min-width: ${screen('md')}) {
    font-size: ${fontSize('xl6')};
  }

  @media (min-width: ${screen('lg')}) {
    font-size: ${fontSize('xl7')};
  }
`;

export const StyledHeader = styled.div`
  text-align: center;
  max-width: ${container('md')};
  margin: auto;
  line-height: 1.5;

  @media (min-width: ${screen('md')}) {
    font-size: ${fontSize('lg')};
    line-height: 1.6;
  }
`;

export const StyledCardTitle = styled.h4`
  margin: 0;
  font-size: ${fontSize('md')};
  line-height: 1.25;
  text-transform: uppercase;
  font-weight: ${fontWeight('extraBold')};
  color: ${color('text_subtle')};
`;

export const StyledHighlightList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing('md')} ${spacing('xl')};
  max-width: ${container('lg')};
  margin-left: auto;
  margin-right: auto;

  ${StyledCardTitle} {
    text-align: center;
    width: 100%;
    color: inherit;
  }
`;

export const StyledHighlight = styled.div`
  background-color: ${color('indigo_700')};
  color: ${color('white')};
  padding: ${spacing('md')};
  border-radius: ${radius('md')};
  display: flex;
  align-items: center;
  gap: ${spacing('md')};
  font-weight: ${fontWeight('medium')};
  line-height: 1.5;
  width: 100%;

  @media (min-width: ${screen('md')}) {
    width: 47%;
  }

  @media (min-width: ${screen('lg')}) {
    width: 31%;
  }
`;

export const StyledHighlightValue = styled.div`
  font-family: ${fontFamily('display')};
  font-size: ${fontSize('xl5')};
  line-height: 1;
  font-weight: ${fontWeight('semiBold')};
  text-transform: uppercase;
  min-width: 1.25em;
  text-align: right;
`;

export const StyledContent = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${spacing('xl')};
  margin-top: ${spacing('xl3')};
  margin-bottom: ${spacing('xl3')};

  @media (min-width: ${screen('md')}) {
    grid-template-columns: 2fr 1fr;
  }

  &.reverse {
    grid-template-columns: minmax(0, 1fr);

    @media (min-width: ${screen('md')}) {
      grid-template-columns: 1fr 2fr;
    }
  }

  .text-lg {
    line-height: 1.5;

    @media (min-width: ${screen('md')}) {
      font-size: ${fontSize('lg')};
      line-height: 1.6;
    }
  }

  > div {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: ${spacing('xl')};

    ${StyledHighlight} {
      width: 100% !important;
    }

    ${StyledHighlightList} {
      gap: ${spacing('xs')};

      @media (min-width: ${screen('md')}) {
        flex-wrap: nowrap;
      }

      ${StyledHighlight} {
        flex-direction: column;
        text-align: center;
        width: 50%;
      }
    }
  }
`;

export const StyledCard = styled.div`
  width: 100%;
  background-color: ${color('white')};
  padding: ${spacing('xl2')} ${spacing('xl')};
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: ${spacing('xl2')};
  border-radius: ${radius('lg')};
  color: ${color('text_default')};
`;

export const StyledDataItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing('lg')};
  font-weight: ${fontWeight('extraBold')};
`;

export const StyledData = styled.div`
  display: flex;
  gap: ${spacing('xl')};
  justify-content: center;
  flex-direction: column;

  @media (min-width: ${screen('md')}) {
    flex-direction: row;
  }

  &.vertical {
    flex-direction: column;
  }
`;

export const StyledValue = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  color: ${color('indigo_500')};
  font-size: ${fontSize('lg')};

  &::after {
    content: '';
    width: ${spacing('xl4')};
    height: ${spacing('xs')};
    background-color: ${color('indigo_300')};
    position: absolute;
    bottom: -${spacing('sm')};
    left: 50%;
    transform: translateX(-50%);
  }
`;

export const StyledValueNumber = styled.span`
  font-family: ${fontFamily('display')};
  font-size: ${fontSize('xl6')};
  line-height: 1;
  font-weight: ${fontWeight('semiBold')};
`;

export const StyledSource = styled.div`
  font-size: ${fontSize('xs')};
  line-height: 1.333;
  color: ${color('text_subtle')};
  text-align: center;
  margin-top: ${spacing('sm')};
  strong {
    font-weight: ${fontWeight('extraBold')};
  }
`;

export const StyledSatisfactionContent = styled.div`
  max-width: ${container('lg')};
  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacing('xl')};
  margin: ${spacing('xl3')} auto;
  line-height: 1.5;

  @media (min-width: ${screen('md')}) {
    grid-template-columns: 3fr 1fr;
    font-size: ${fontSize('lg')};
    line-height: 1.6;
  }

  ${Heading2} {
    text-align: left;
    margin-top: 0;
  }

  li {
    margin-bottom: ${spacing('lg')};
    background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12 0C5.37256 0 0 5.37256 0 12C0 18.6274 5.37256 24 12 24C18.6274 24 24 18.6274 24 12C24 5.37256 18.6274 0 12 0ZM12 22.4516C6.25819 22.4516 1.54839 17.8038 1.54839 12C1.54839 6.25824 6.19621 1.54839 12 1.54839C17.7418 1.54839 22.4516 6.19621 22.4516 12C22.4516 17.7418 17.8038 22.4516 12 22.4516ZM18.8531 9.14705L10.1202 17.8099C9.8925 18.0357 9.52485 18.0343 9.29903 17.8066L5.14365 13.6176C4.91782 13.39 4.91927 13.0223 5.14694 12.7965L5.55919 12.3876C5.78685 12.1618 6.1545 12.1632 6.38032 12.3909L9.71787 15.7554L17.6263 7.91037C17.854 7.68455 18.2216 7.686 18.4474 7.91366L18.8564 8.32592C19.0822 8.55358 19.0807 8.92123 18.8531 9.14705V9.14705Z' fill='%23C0BEC9'/%3E%3C/svg%3E%0A");
    background-repeat: no-repeat;
    background-position: left top;
    padding-left: ${spacing('xl2')};

    @media (min-width: ${screen('md')}) {
      background-position: left top 3px;
    }
  }
`;

export const StyledNps = styled.div`
  text-align: center;
  margin-top: 0;
`;

export const StyledNpsChart = styled.div`
  position: relative;

  ${StyledValueNumber} {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -45%);
    z-index: -1;
  }

  li {
    margin-bottom: 0;
    background-image: unset;
    padding-left: 0;
  }
`;

export const StyledNpsItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing('md')};
  margin-top: ${spacing('xl')};
  font-size: ${fontSize('md')};
  line-height: 1.5;
`;
